import { useEffect } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import MDBox from 'components/MDBox';
import { useGetChatAssistantsQuery } from 'services/assistantApi';
import { useNotification } from 'hook/useNotification';

/**
 * Displays a dropdown for selecting an AI assistant.
 *
 * @param {Object} selectedAssistant - The currently selected assistant, identified by its code.
 * @param {Function} setSelectedAssistant - Function to set the selected assistant.
 *
 * @returns {JSX.Element} The rendered assistant selection component.
 */
export default function ChatBotAssistantSelection({
  selectedAssistant,
  onAssistantChange,
  isPublic = false
}) {
  const { formatMessage: msg } = useIntl();
  const { addNotification } = useNotification();
  const { data: assistants, error, isLoading } = useGetChatAssistantsQuery({ isPublic });

  useEffect(() => {
    if (
      assistants?.length > 0 &&
      (!selectedAssistant ||
        !assistants.some((assistant) => assistant.code === selectedAssistant.code))
    ) {
      onAssistantChange(assistants[0]);
    }
  }, [assistants, selectedAssistant, onAssistantChange]);

  const handleChange = (event) => {
    const newAssistantCode = event.target.value;
    onAssistantChange(assistants.find((assistant) => assistant.code === newAssistantCode));
  };

  if (isLoading) {
    return (
      <MDBox
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <CircularProgress />
        <p>{msg({ id: 'page.chatbox.assistant.loading' })}</p>
      </MDBox>
    );
  }

  if (error) {
    if ('status' in error) {
      console.error(`Erreur ${error.status}: ${JSON.stringify(error.data)}`);
    } else if (error.message) {
      console.error(error.message);
    }
    addNotification({
      type: 'error',
      title: msg({ id: 'notification.error.title' }),
      message: msg({ id: 'page.chatbox.error.assistant.error' })
    });
  }

  return (
    <FormControl fullWidth variant="outlined">
      <InputLabel id="assistant-select-label">Assistant</InputLabel>
      <Select
        id="assistant-select"
        value={selectedAssistant?.code || ''}
        onChange={handleChange}
        label="Assistant"
        variant="outlined"
        fullWidth
      >
        <MenuItem value="" disabled>
          Sélectionnez un assistant
        </MenuItem>
        {assistants.map((assistant) => (
          <MenuItem key={assistant.id} value={assistant.code}>
            {assistant.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

ChatBotAssistantSelection.propTypes = {
  selectedAssistant: PropTypes.shape({
    code: PropTypes.string,
    label: PropTypes.string,
    id: PropTypes.number
  }),
  onAssistantChange: PropTypes.func.isRequired,
  isPublic: PropTypes.bool
};
